import { DocumentNumberUtils } from '@/chassis/core/utils/documentnumber-utils';

/**
 * Composicao societaria do fornecedor (cdcb-103). Mesmas regras do backend
 * (opd-suppliers/application/services/supplier-partners.applicationservice.ts):
 * nome obrigatorio, CPF/CNPJ valido (estrangeiro so preenchido), sem documento
 * repetido, percentual > 0 e <= 100 e soma 100%. Lista vazia e aceita.
 */

export type SupplierPartnerDocumentType = 'CPF' | 'CNPJ' | 'ESTRANGEIRO' | '';

export interface SupplierPartner {
    documentType: SupplierPartnerDocumentType;
    documentNumber: string;
    name: string;
    role: string;
    /** Percentual de participacao. Texto enquanto o usuario digita ("33,5"); numero ao salvar. */
    sharePercentage?: number | string | null;
}

/** Tolerancia da soma: 33,33 + 33,33 + 33,34 fecha 100. */
const SUM_TOLERANCE = 0.01;

/** Le numero ou texto com virgula decimal; NaN quando vazio ou ilegivel. */
export function parseSharePercentage(value: SupplierPartner['sharePercentage']): number {
    if (typeof value === 'number') return value;
    const text = String(value ?? '').trim().replace('%', '').replace(',', '.');
    return text === '' ? NaN : Number(text);
}

export function maskPartnerDocument(documentType: SupplierPartnerDocumentType, value: string): string {
    if (documentType === 'CPF') return DocumentNumberUtils.maskCpf(value);
    if (documentType === 'CNPJ') return DocumentNumberUtils.maskCnpj(value);
    return value;
}

/** Documento como o backend grava: so digitos para CPF/CNPJ. */
export function normalizePartnerDocument(documentType: SupplierPartnerDocumentType, value: string): string {
    if (documentType === 'CPF') return DocumentNumberUtils.removeCpfMask(value);
    if (documentType === 'CNPJ') return DocumentNumberUtils.removeCnpjMask(value);
    return String(value ?? '').trim();
}

/** Linha sem nada preenchido: o service descarta ao salvar, entao a validacao tambem ignora. */
export function isBlankPartner(partner: SupplierPartner): boolean {
    return !String(partner?.name ?? '').trim()
        && !String(partner?.documentNumber ?? '').trim()
        && !Number.isFinite(parseSharePercentage(partner?.sharePercentage));
}

export function sumSharePercentages(partners: SupplierPartner[]): number {
    const total = partners.reduce((acc, partner) => {
        const value = parseSharePercentage(partner.sharePercentage);
        return Number.isFinite(value) ? acc + value : acc;
    }, 0);
    return Math.round(total * 100) / 100;
}

export function formatPercentage(value: number): string {
    return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
}

export interface PartnersValidation {
    /** Erros por linha (indice do socio). */
    rowErrors: Record<number, string[]>;
    /** Erro da lista inteira (soma). */
    totalError: string | null;
    total: number;
    valid: boolean;
}

export const REQUIRED_PARTNERS_MESSAGE = 'Informe ao menos 1 sócio, com os percentuais de participação somando 100%.';

/**
 * `required` (cdcb-104): fornecedor novo cadastrado pelo proprio usuario precisa
 * de ao menos 1 socio; sem ele a lista vazia continua valida (fornecedor antigo).
 */
export function validatePartners(partners: SupplierPartner[], { required = false }: { required?: boolean } = {}): PartnersValidation {
    const rowErrors: Record<number, string[]> = {};
    const push = (index: number, message: string) => {
        (rowErrors[index] ??= []).push(message);
    };
    const seen = new Map<string, number>();
    let allPercentagesValid = true;
    const filled = partners.filter((partner) => !isBlankPartner(partner));

    partners.forEach((partner, index) => {
        if (isBlankPartner(partner)) return;
        const documentType = (partner.documentType || 'CPF') as SupplierPartnerDocumentType;
        if (!String(partner.name ?? '').trim()) push(index, 'Informe o nome do sócio.');

        const documentNumber = normalizePartnerDocument(documentType, partner.documentNumber);
        if (documentType === 'CPF' && !DocumentNumberUtils.isValidCpf(documentNumber)) push(index, 'CPF inválido.');
        else if (documentType === 'CNPJ' && !DocumentNumberUtils.isValidCnpj(documentNumber)) push(index, 'CNPJ inválido.');
        else if (documentType === 'ESTRANGEIRO' && !documentNumber) push(index, 'Informe o documento estrangeiro.');

        if (documentNumber) {
            const key = `${documentType}:${documentNumber.toUpperCase()}`;
            const first = seen.get(key);
            if (first !== undefined) push(index, `Documento repetido (igual ao do sócio ${first + 1}).`);
            else seen.set(key, index);
        }

        const share = parseSharePercentage(partner.sharePercentage);
        if (!Number.isFinite(share)) {
            push(index, 'Informe o percentual de participação.');
            allPercentagesValid = false;
        } else if (share <= 0 || share > 100) {
            push(index, 'O percentual deve ser maior que 0 e no máximo 100.');
            allPercentagesValid = false;
        }
    });

    const total = sumSharePercentages(filled);
    let totalError: string | null = null;
    if (required && filled.length === 0) totalError = REQUIRED_PARTNERS_MESSAGE;
    else if (filled.length > 0 && allPercentagesValid && Math.abs(total - 100) > SUM_TOLERANCE) {
        totalError = `A soma dos percentuais de participação deve ser 100% (hoje está em ${formatPercentage(total)}).`;
    }

    return { rowErrors, totalError, total, valid: Object.keys(rowErrors).length === 0 && !totalError };
}

/**
 * Lista como o backend grava: so linhas com nome, documento ou percentual; campos
 * aparados, CPF/CNPJ sem mascara e percentual numerico.
 */
export function toPartnersPayload(partners: SupplierPartner[]): SupplierPartner[] {
    return partners
        .map((partner) => {
            const documentType = (partner?.documentType || 'CPF') as SupplierPartnerDocumentType;
            const share = parseSharePercentage(partner?.sharePercentage);
            return {
                documentType,
                documentNumber: normalizePartnerDocument(documentType, String(partner?.documentNumber || '')),
                name: String(partner?.name || '').trim(),
                role: String(partner?.role || '').trim(),
                sharePercentage: Number.isFinite(share) ? share : null,
            };
        })
        .filter((partner) => partner.name || partner.documentNumber || partner.sharePercentage !== null);
}

/** Mensagem unica para o aviso de envio bloqueado. */
export function describePartnersErrors(validation: PartnersValidation): string {
    const lines = Object.entries(validation.rowErrors).map(([index, errors]) => `Sócio ${Number(index) + 1}: ${errors.join(' ')}`);
    if (validation.totalError) lines.push(validation.totalError);
    return `Composição societária: ${lines.join(' ')}`;
}

/**
 * Cadastro completo para o aviso aos fornecedores antigos: pelo menos um socio
 * e a lista valida (percentuais fechando 100%).
 */
export function hasCompletePartners(partners: unknown): boolean {
    if (!Array.isArray(partners)) return false;
    const filled = (partners as SupplierPartner[]).filter((partner) => !isBlankPartner(partner));
    return filled.length > 0 && validatePartners(filled).valid;
}
